import React from "react";

import { css, cx } from "@emotion/css";

import { useStyles } from "@/hooks/useStyles.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { Typography } from "@/uiKit/atoms/Typography.tsx";
import type { AtomProps, StyleClasses } from "@/uiKit/atoms/utils/AtomTypes.ts";

export type BaseStatItemProps = {
  title: string;
  value: number;
  precision?: number;
  unit?: string;
  variation?: number;
  lowerIsBetter?: boolean;
  variationThreshold?: number;
};

export type StatItemProps = AtomProps<BaseStatItemProps, StyleClasses<"root" | "title" | "value" | "variation">>;

export const StatItem: React.FC<StatItemProps> = ({
  title,
  value,
  unit,
  variation,
  lowerIsBetter = false,
  variationThreshold = 0,
  precision = 2,
  className,
  classes,
}) => {
  const styles = useStyles(makeStyles, variation, lowerIsBetter, variationThreshold);
  const variationIcon = !variation ? "=" : variation > 0 ? "▲" : "▼";
  return (
    <div className={cx(styles.root, className, classes?.root)}>
      <Typography variant="body" className={cx(styles.title, classes?.title)}>
        {title}
      </Typography>
      <Typography variant="subtitleLarge" component="span" className={cx(styles.value, classes?.value)}>
        {value.toFixed(precision)}
        <Typography variant="body" component="span">
          {` ${unit ?? ""}`}
        </Typography>
      </Typography>

      <Typography variant="bodySmall" component="span" className={cx(styles.variation, classes?.variation)}>
        {`${variationIcon} ${variation !== undefined ? variation.toFixed(precision) : ""}  ${unit ?? ""}`}
      </Typography>
    </div>
  );
};

const makeStyles = (
  theme: Theme,
  variation: number | undefined,
  lowerIsBetter: boolean,
  variationThreshold: number,
) => ({
  root: css({
    display: "flex",
    flexDirection: "column",
  }),
  title: css({
    whiteSpace: "nowrap",
    overflow: "clip",
    textOverflow: "ellipsis",
  }),
  value: css({
    marginTop: theme.spacing(8),
    marginBottom: theme.spacing(2),
    fontWeight: "bold",
  }),
  variation: css({
    color: !variation
      ? "transparent"
      : (!lowerIsBetter && variation > variationThreshold) || (lowerIsBetter && variation < variationThreshold)
        ? theme.palette.success.main
        : theme.palette.error.main,
    visibility: !variation ? "hidden" : "visible",
    fontWeight: "bold",
  }),
});
